import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
// axios / API_PATHS not used directly in this dialog (API helpers are used)
import {updateResumeAPI, createResumeAPI } from '../../api/controller/videoController/resume';

const TRANSPORT_OPTIONS = {
    Public: 'Public Transit Accessible',
    Private: 'Requires Personal Vehicle',
};

const CATEGORY_OPTIONS = [
    'All crimes against a person',
    'Armed Criminal Action (crimes involving a gun)',
    'Crimes within the last 7 years',
    'Crimes within the last 3 years',
    'Drug-related offenses',
    'Embezzlement',
    'Fraud',
    'Murder (1st degree)',
    'Murder (1st and 2nd degree)',
    'Offense involving children',
    'Sexual offense (All)',
    'Sexual offense (involving children)',
    'Theft',
    'Violent crimes',
];
const CATEGORY_SELECT_ALL = 'Select All';

const MAX_VIDEO_SIZE_BYTES = 8 * 1024 * 1024; // 8 MB
const MAX_DOCUMENT_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

const INITIAL_RESUME_STATE = {
    id: '',
    _id: '',
    name: '',
    video: null,
    pdf: null,
    skills: [],
    category: [],
    transport: [],
    industry: '',
};

const getTransportValue = (keyOrValue) => {
    if (!keyOrValue) return '';
    if (TRANSPORT_OPTIONS[keyOrValue]) return TRANSPORT_OPTIONS[keyOrValue];
    const match = Object.entries(TRANSPORT_OPTIONS).find(([, value]) => value === keyOrValue);
    return match ? match[1] : keyOrValue;
};

const getTransportKeyFromValue = (value) => {
    if (!value) return '';
    const match = Object.entries(TRANSPORT_OPTIONS).find(([, label]) => label === value);
    return match ? match[0] : value;
};
const ResumeDialog = ({ showModal, setShowModal, onCreateResume, onEditResume, selectedResume, industries }) => {
    const [resume, setResume] = useState(() => (selectedResume ? { ...selectedResume } : { ...INITIAL_RESUME_STATE }));
    const [error, setError] = useState(null);
    const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
    const [categoryFilters, setCategoryFilters] = useState(Array.isArray(selectedResume?.category) ? selectedResume.category : []);
    const [transportMenuOpen, setTransportMenuOpen] = useState(false);
    const [transportFilters, setTransportFilters] = useState(
        Array.isArray(selectedResume?.transport)
            ? selectedResume.transport.map((entry) => getTransportKeyFromValue(entry)).filter(Boolean)
            : (typeof selectedResume?.transport === 'string' && selectedResume.transport
                ? selectedResume.transport.split(',').map((s) => getTransportKeyFromValue(s.trim())).filter(Boolean)
                : [])
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleCategoryToggle = (option) => {
        setCategoryFilters((prev) => {
            let next;
            if (option === CATEGORY_SELECT_ALL) {
                const isAllSelected = prev.length === CATEGORY_OPTIONS.length;
                next = isAllSelected ? [] : [...CATEGORY_OPTIONS];
            } else {
                next = prev.includes(option)
                    ? prev.filter((v) => v !== option)
                    : [...prev, option];
                if (next.length === CATEGORY_OPTIONS.length) {
                    next = [...CATEGORY_OPTIONS];
                }
            }
            setResume((r) => ({ ...r, category: next }));
            return next;
        });
    };

    const handleTransportToggle = (optionValue) => {
        setTransportFilters((prev) => {
            const next = prev.includes(optionValue)
                ? prev.filter((v) => v !== optionValue)
                : [...prev, optionValue];
            // keep resume.transport in sync
            setResume((r) => ({ ...r, transport: next }));
            return next;
        });
    };

    const syncFormFromSelected = useCallback(() => {
        if (selectedResume) {
            const skills = Array.isArray(selectedResume.skills)
                ? selectedResume.skills.map((skill) => (typeof skill === 'string' ? skill.trim() : skill)).filter(Boolean)
                : [];

            let categories = [];
            if (Array.isArray(selectedResume.category)) categories = selectedResume.category;
            else if (typeof selectedResume.category === 'string' && selectedResume.category.trim() !== '') {
                categories = selectedResume.category.split(',').map((c) => c.trim()).filter(Boolean);
            }
            const normalizedCategories = categories.filter((item) => CATEGORY_OPTIONS.includes(item));

            const industryOptions = Array.isArray(industries) ? industries : [];
            const extractId = (value) => {
                if (!value) return '';
                if (typeof value === 'object') {
                    return (value._id || value.industryId || value.id || '').toString();
                }
                return value.toString();
            };

            const selectedRaw = extractId(selectedResume.industry);
            let industryValue = '';
            if (selectedRaw) {
                const matchById = industryOptions.find((ind) => ind?.industryId && ind.industryId.toString() === selectedRaw);
                if (matchById) {
                    industryValue = matchById.industryId;
                }
            }
            if (!industryValue) {
                const labelCandidate = `${selectedResume.industry ?? ''} at ${selectedResume.location ?? ''}`.trim();
                const matchByLabel = industryOptions.find((ind) => ind?.industry && ind.industry === labelCandidate);
                if (matchByLabel) {
                    industryValue = matchByLabel.industryId;
                }
            }
            if (!industryValue) {
                industryValue = selectedRaw;
            }

            let tfilters = [];
            if (Array.isArray(selectedResume.transport)) tfilters = selectedResume.transport;
            else if (typeof selectedResume.transport === 'string' && selectedResume.transport.trim() !== '') {
                tfilters = selectedResume.transport.split(',').map((t) => t.trim()).filter(Boolean);
            }
            const normalizedTransportFilters = tfilters.map((entry) => getTransportKeyFromValue(entry)).filter(Boolean);

            setResume({
                ...selectedResume,
                skills,
                category: normalizedCategories,
                industry: industryValue,
                transport: normalizedTransportFilters,
                video: null,
                pdf: null,
            });
            setCategoryFilters(normalizedCategories);
            setTransportFilters(normalizedTransportFilters);
        } else {
            setResume({ ...INITIAL_RESUME_STATE });
            setCategoryFilters([]);
            setTransportFilters([]);
        }
    }, [selectedResume, industries]);

    useEffect(() => {
        syncFormFromSelected();
    }, [syncFormFromSelected]);

    const resolveUrl = (...candidates) => {
        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate.trim();
            }
        }
        return '';
    };

    const extractFileName = (url) => {
        if (!url) return '';
        try {
            const withoutQuery = url.split('?')[0];
            const parts = withoutQuery.split('/');
            const lastPart = parts[parts.length - 1];
            return lastPart || url;
        } catch (err) {
            return url;
        }
    };

    const existingVideoUrl = resolveUrl(
        selectedResume?.videoURL,
        selectedResume?.videoUrl,
        selectedResume?.video,
        selectedResume?.videoLink,
        selectedResume?.videoPath
    );

    const existingPdfUrl = resolveUrl(
        selectedResume?.pdfURL,
        selectedResume?.resumeURL,
        selectedResume?.resume,
        selectedResume?.fileURL,
        selectedResume?.pdf
    );

    const hasStoredVideo = Boolean(existingVideoUrl);

    const hasStoredPdf = Boolean(existingPdfUrl);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        // support multi-select for category
        if (name === 'category') {
            const options = e.target.options;
            const selected = [];
            for (let i = 0; i < options.length; i++) {
                if (options[i].selected) selected.push(options[i].value);
            }
            setResume((prev) => ({ ...prev, category: selected }));
            return;
        }

        if ((name === 'video' || name === 'pdf') && files && files[0]) {
            const file = files[0];
            const sizeLimit = name === 'video' ? MAX_VIDEO_SIZE_BYTES : MAX_DOCUMENT_SIZE_BYTES;
            if (file.size > sizeLimit) {
                const limitLabel = name === 'video' ? '8 MB' : '5 MB';
                setError(`${name === 'video' ? 'Video' : 'Document'} must be ${limitLabel} or smaller.`);
                e.target.value = '';
                setResume((prev) => ({ ...prev, [name]: null }));
                return;
            }
            setError(null);
        }

        setResume((prev) => ({
            ...prev,
            // support both video and pdf file inputs
            [name]: name === 'video' || name === 'pdf' ? (files && files[0]) : value,
        }));
    };
    

    const handleSkillsChange = (e) => {
        const { value } = e.target;
        setResume((prev) => ({
            ...prev,
            skills: value
                ? value
                    .split(',')
                    .map((skill) => skill.trim())
                    .filter(Boolean)
                : [],
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (isSubmitting) return;

        const hasName = Boolean(resume.name && resume.name.trim());
        const hasVideo = Boolean(resume.video) || hasStoredVideo;
        const hasPdf = Boolean(resume.pdf) || hasStoredPdf;
        const skillList = Array.isArray(resume.skills) ? resume.skills.filter((skill) => skill && skill.trim()) : [];
        const categoryList = Array.isArray(resume.category) ? resume.category.filter(Boolean) : [];
        const transportList = Array.isArray(resume.transport) ? resume.transport.filter(Boolean) : [];
        const hasIndustry = Boolean(resume.industry);

        const missing = [];
        if (!hasName) missing.push('Name');
        if (!hasVideo) missing.push('Video');
        if (!hasPdf) missing.push('Resume file');
        if (skillList.length === 0) missing.push('Skills');
        if (categoryList.length === 0) missing.push('Category');
        if (transportList.length === 0) missing.push('Transport');
        if (!hasIndustry) missing.push('Industry');

        if (missing.length > 0) {
            const pretty = missing.map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase());
            const last = pretty[pretty.length - 1];
            const formatted = pretty.length > 1 ? `${pretty.slice(0, -1).join(', ')} and ${last}` : pretty[0];
            setError(`Please provide ${formatted}.`);
            return;
        }

        if (resume.video && resume.video.size > MAX_VIDEO_SIZE_BYTES) {
            setError('Video must be 8 MB or smaller.');
            return;
        }

        if (resume.pdf && resume.pdf.size > MAX_DOCUMENT_SIZE_BYTES) {
            setError('Document must be 5 MB or smaller.');
            return;
        }

        try {
            setIsSubmitting(true);
                if (selectedResume) {
                // const updatedResume = {
                //     ...selectedResume,
                //     name: resume.name?.trim() || selectedResume.name,
                //     video: resume.video,
                //     skills: resume.skills.length > 0 ? resume.skills.join(',') : selectedResume.skills,
                //     category: resume.category?.trim() || selectedResume.category,
                //     transport: resume.transport?.trim() || selectedResume.transport,
                //     industry: resume.industry?.trim() || selectedResume.industry,
                // };
                // normalize category and skills for update payload
                const payload = {
                    ...resume,
                    category: Array.isArray(resume.category) ? resume.category.join(',') : (resume.category || ''),
                    skills: Array.isArray(resume.skills) ? resume.skills.join(',') : resume.skills,
                    transport: Array.isArray(resume.transport) ? resume.transport.join(',') : resume.transport,
                };
                await updateResumeAPI(payload);
                await onEditResume(payload);
            } else {
                const formData = new FormData();
                formData.append('name', resume.name);
                formData.append('video', resume.video);
                formData.append('skills', resume.skills.join(','));
                formData.append('category', Array.isArray(resume.category) ? resume.category.join(',') : (resume.category || ''));
                formData.append('transport', Array.isArray(resume.transport) ? resume.transport.join(',') : resume.transport);
                formData.append('industry', resume.industry);
                formData.append('pdf', resume.pdf);

                const response = await createResumeAPI(formData);
                onCreateResume(response.data);
            }

            setResume({ ...INITIAL_RESUME_STATE });
            setCategoryFilters([]);
            setTransportFilters([]);
            setCategoryMenuOpen(false);
            setTransportMenuOpen(false);
            setShowModal(false);
        } catch (error) {
            console.error('Error submitting resume:', error);
            setError(error.response?.data?.message || 'Failed to submit resume. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const transportSummary = (() => {
        if (!transportFilters || transportFilters.length === 0) return 'Select transport';
        const selectedValues = transportFilters
            .map((key) => getTransportValue(key))
            .filter(Boolean);
        return selectedValues.length > 0 ? selectedValues.join(', ') : 'Select transport';
    })();

    const handleClose = () => {
        if (isSubmitting) return;
        syncFormFromSelected();
        setCategoryMenuOpen(false);
        setTransportMenuOpen(false);
        setError(null);
        setShowModal(false);
    };

    if (!showModal) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="absolute inset-0 bg-black opacity-50" onClick={handleClose}></div>
            <div className="relative bg-white rounded-lg p-6 w-full max-w-md shadow-lg z-10 max-h-[90vh] overflow-y-auto">
                <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-3xl font-bold leading-none"
                    aria-label="Close dialog"
                    disabled={isSubmitting}
                >
                    &times;
                </button>
                <h2 className="text-xl font-bold mb-4">{selectedResume ? 'Edit Resume' : 'Add Resume'}</h2>
                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Name:</label>
                        <input
                            type="text"
                            name="name"
                            value={resume.name}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg p-2"
                            disabled={isSubmitting}
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Video:</label>
                        <input
                            type="file"
                            name="video"
                            accept="video/*"
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg p-2"
                            disabled={isSubmitting}
                            required={!hasStoredVideo}
                        />
                        <p className="mt-1 text-xs text-gray-500">Maximum size: 8 MB.</p>
                        {hasStoredVideo ? (
                            <p className="mt-2 text-xs text-gray-600">
                                Current file: <a href={existingVideoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{extractFileName(existingVideoUrl) || 'View video'}</a>
                            </p>
                        ) : null}
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Resume File (Pdf,Docx,Webp):</label>
                        <input
                            type="file"
                            name="pdf"
                            accept="application/pdf, image/*, image/webp, .doc, .docx, .ppt, .pptx, .xls, .xlsx"
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg p-2"
                            disabled={isSubmitting}
                            required={!hasStoredPdf}
                        />
                        <p className="mt-1 text-xs text-gray-500">Maximum size: 5 MB.</p>
                        {hasStoredPdf ? (
                            <p className="mt-2 text-xs text-gray-600">
                                Current file: <a href={existingPdfUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{extractFileName(existingPdfUrl) || 'View file'}</a>
                            </p>
                        ) : null}
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Skills (comma-separated):</label>
                        <input
                            type="text"
                            name="skills"
                            value={resume.skills.join(', ')}
                            onChange={handleSkillsChange}
                            className="w-full border border-gray-300 rounded-lg p-2"
                            required
                            disabled={isSubmitting}
                        />
                    </div>
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium mb-2">Category:</label>
                                                <div className="relative">
                                                        <button
                                                                type="button"
                                                            onClick={() => !isSubmitting && setCategoryMenuOpen((s) => !s)}
                                                            className="w-full text-left border border-gray-300 rounded-lg p-2"
                                                            disabled={isSubmitting}
                                                        >
                                                                {categoryFilters && categoryFilters.length > 0 ? `${categoryFilters.length} selected` : 'Select categories'}
                                                        </button>
                                                        {categoryMenuOpen && !isSubmitting && (
                                                            <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-lg p-2 max-h-64 overflow-auto">
                                                                {[CATEGORY_SELECT_ALL, ...CATEGORY_OPTIONS].map((option) => {
                                                                    const optionKey = option.toLowerCase().replace(/\s+/g, '-');
                                                                    const isSelectAll = option === CATEGORY_SELECT_ALL;
                                                                    const isChecked = isSelectAll
                                                                        ? categoryFilters.length === CATEGORY_OPTIONS.length
                                                                        : categoryFilters.includes(option);
                                                                    return (
                                                                        <label key={optionKey} className="flex items-center gap-2 p-1">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="h-4 w-4"
                                                                                checked={isChecked}
                                                                                onChange={() => handleCategoryToggle(option)}
                                                                            />
                                                                            <span className="text-sm text-[#003DA5]">{option}</span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                </div>
                                        </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Transport:</label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => !isSubmitting && setTransportMenuOpen((s) => !s)}
                                className="w-full text-left border border-gray-300 rounded-lg p-2"
                                disabled={isSubmitting}
                            >
                                {transportSummary}
                            </button>
                            {transportMenuOpen && !isSubmitting && (
                                <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-lg p-2 max-h-48 overflow-auto">
                                    {Object.entries(TRANSPORT_OPTIONS).map(([optionKey, optionValue]) => {
                                        const isChecked = transportFilters.includes(optionKey);
                                        return (
                                            <label key={optionKey} className="flex items-center gap-2 p-1">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4"
                                                    checked={isChecked}
                                                    onChange={() => handleTransportToggle(optionKey)}
                                                />
                                                <span className="text-sm text-[#003DA5]">
                                                    {optionValue}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Industry:</label>
                        <select
                            name="industry"
                            value={resume.industry }
                            onChange={(e) => {
                                const selectedValue = e.target.value;
                                setResume((prev) => ({ ...prev, industry: selectedValue }));
                            }}
                            className="w-full border border-gray-300 rounded-lg p-2"
                            required
                            disabled={isSubmitting}
                        >
                            <option value="">Select Industry</option>
                                    {Array.isArray(industries) && industries.map((ind) => (
                                        <option key={ind.industryId} value={ind.industryId}>
                                            {ind.industry}
                                        </option>
                                    ))}
                        </select>
                    </div>

                    {/* Description and Favorite fields removed */}
                    <div className="flex justify-end gap-4">
                        <button
                            type="button"
                            onClick={handleClose}
                            className={`bg-gray-300 text-gray-700 px-4 py-2 rounded-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 ${isSubmitting ? 'opacity-80 cursor-not-allowed' : ''}`}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                                    {selectedResume ? 'Saving...' : 'Adding...'}
                                </>
                            ) : (
                                selectedResume ? 'Update Resume' : 'Add Resume'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

ResumeDialog.propTypes = {
    showModal: PropTypes.bool.isRequired,
    setShowModal: PropTypes.func.isRequired,
    onCreateResume: PropTypes.func.isRequired,
    onEditResume: PropTypes.func.isRequired,
    selectedResume: PropTypes.object,
    industries: PropTypes.arrayOf(
        PropTypes.shape({
            industryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            industry: PropTypes.string.isRequired,
        })
    ).isRequired,
};

export default ResumeDialog;